import React, {useEffect, useMemo, useState} from "react";
import {Box, Button, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useMediaQuery} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import {useNavigate} from "react-router-dom";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import {
	ExpiredCandidatesDto,
	ExpiredDocument,
	ExpiredDocumentsDto,
	PersonDocumentRetentionRestControllerApiFactory,
	UserAuthItemTypeEnum,
} from "arteco-api-client-ts";
import {useApiManager} from "../../../../components/utils/api";
import {useHasAnyRole} from "../../../../components/utils/auth";
import {dateToString} from "../../../../components/utils/dates";
import {
	btnSx,
	EmptyState,
	FilterChips,
	FilterOption,
	ListCard,
	Page,
	PageHeader,
	Panel,
	SkeletonRows,
	SoftChip,
	StatGrid,
	StatTile,
	tableContainerSx,
	tableSx,
} from "../../../../components/kit";
import {COMPLIANCE, useComplianceAccess} from "../access";

const Person = ({d}: { d: ExpiredDocument }) => (
	<Stack direction="row" spacing={1} alignItems="center" sx={{minWidth: 0}}>
		<Typography variant="body2" fontWeight={600} noWrap>{d.personName}</Typography>
		{!d.personActive && <SoftChip tone="neutral" label="Baja"/>}
	</Stack>
);

const Rule = ({d}: { d: ExpiredDocument }) => (
	<Box sx={{minWidth: 0}}>
		<Typography variant="body2">{d.retentionName}{d.treatment ? ` · ${d.treatment}` : ""}</Typography>
		<Typography variant="caption" color="text.secondary">{d.reason}</Typography>
	</Box>
);

/**
 * Candidatos de selección sin contacto desde hace más de su plazo (#14006): el registro de tratamientos
 * (T-05) da un año salvo consentimiento para más. Quien tiene selección abre la ficha y decide si se borra.
 */
const ExpiredCandidatesPanel = () => {
	const apiManager = useApiManager();
	const ctrl = apiManager.factory(PersonDocumentRetentionRestControllerApiFactory);
	const navigate = useNavigate();
	const canOpen = useHasAnyRole(UserAuthItemTypeEnum.ADMIN, UserAuthItemTypeEnum.ADMIN_RECRUITMENT);
	const [data, setData] = useState<ExpiredCandidatesDto>();
	const [error, setError] = useState(false);

	useEffect(() => {
		apiManager.execute(ctrl.expiredCandidatesPersonDocumentRetention()).then(setData).catch(() => setError(true));
	}, []);

	const rows = data?.rows || [];
	const rule = data ? `${data.ruleName || "Un año desde el último contacto"} · ${data.treatment || "T-05"}` : "";
	return (
		<Panel title={data ? `${rows.length} ${rows.length === 1 ? "candidato" : "candidatos"} sin contacto en más de ${data.months} meses` : "Candidatos de selección"}
			   subtitle={data ? `${rule}. Último contacto: su alta, su última modificación, sus eventos o sus candidaturas.${canOpen ? " Pulsa uno para abrir su ficha y decidir si se borra." : ""}` : undefined}
			   sx={{mt: 3}}>
			{error ? (
				<EmptyState icon={<ErrorOutlineRoundedIcon/>} title="No se ha podido calcular"
							description="Vuelve a cargar la página; si sigue fallando, avisa con el botón de abajo a la derecha."/>
			) : !data ? <SkeletonRows rows={4}/> : rows.length === 0 ? (
				<EmptyState icon={<TaskAltRoundedIcon/>} title="Ningún candidato fuera de plazo"/>
			) : (
				<Stack spacing={1}>
					{rows.map(c => (
						<ListCard key={c.candidateId}
								  onClick={canOpen ? () => navigate(`/app/admin/recruitment/candidate/${c.candidateId}`) : undefined}>
							<Stack direction={{xs: "column", sm: "row"}} spacing={1} justifyContent="space-between"
								   alignItems={{xs: "flex-start", sm: "center"}}>
								<Box sx={{minWidth: 0}}>
									<Typography variant="body2" fontWeight={600}>{c.name}</Typography>
									<Typography variant="caption" color="text.secondary">
										{c.state ? `${c.state} · ` : ""}último contacto el {dateToString(c.lastContact)}
									</Typography>
								</Box>
								<SoftChip tone="error" label={`Venció ${dateToString(c.dueDate)}`}/>
							</Stack>
						</ListCard>
					))}
				</Stack>
			)}
		</Panel>
	);
};

/**
 * Documentos de persona que han pasado su plazo de conservación (#13996). El sistema no borra nada:
 * quien tiene RR. HH. abre el documento desde aquí y decide. Dirección lo ve en su portada de pendientes y
 * en el resumen de los lunes (#14020).
 */
export const ExpiredDocumentsPage = () => {
	const apiManager = useApiManager();
	const ctrl = apiManager.factory(PersonDocumentRetentionRestControllerApiFactory);
	const theme = useTheme();
	const compact = useMediaQuery(theme.breakpoints.down("md"));
	const navigate = useNavigate();
	const access = useComplianceAccess();
	const [data, setData] = useState<ExpiredDocumentsDto>();
	const [error, setError] = useState(false);
	const [rule, setRule] = useState<number | undefined>(undefined);

	useEffect(() => {
		apiManager.execute(ctrl.expiredPersonDocumentRetention()).then(setData).catch(() => setError(true));
	}, []);

	const all = data?.rows || [];
	const rules = useMemo(() => {
		const m = new Map<number, { name: string, n: number }>();
		all.forEach(d => {
			const k = d.retentionId as number;
			m.set(k, {name: d.retentionName || "", n: (m.get(k)?.n || 0) + 1});
		});
		return [...m.entries()];
	}, [data]);
	const options: FilterOption<number | undefined>[] = [
		{value: undefined, label: `Todos (${all.length})`},
		...rules.map(([id, r]) => ({value: id, label: `${r.name} (${r.n})`})),
	];
	const rows = rule === undefined ? all : all.filter(d => d.retentionId === rule);
	const former = all.filter(d => !d.personActive).length;
	const open = access.hr
		? (d: ExpiredDocument) => navigate(`/app/admin/employee/person/${d.personId}/document/${d.documentId}`) : undefined;

	return (
		<Page>
			<PageHeader title="Documentos fuera de plazo"
						subtitle="Documentos de persona que han pasado su plazo de conservación según el registro de tratamientos. Nada se borra solo."
						actions={<Button size="small" variant="outlined" startIcon={<TuneRoundedIcon/>} sx={btnSx}
										 onClick={() => navigate(`${COMPLIANCE}/retention`)}>Plazos</Button>}/>
			<StatGrid>
				<StatTile loading={!data && !error} icon={<EventBusyOutlinedIcon/>} tone={all.length ? "error" : "success"}
						  label="Fuera de plazo" value={all.length}/>
				<StatTile loading={!data && !error} icon={<ErrorOutlineRoundedIcon/>} tone="neutral"
						  label="De personas de baja" value={former}/>
			</StatGrid>

			<Panel title="Documentos" subtitle={access.hr ? "Pulsa uno para abrirlo en RR. HH. y decidir si se borra." : undefined}>
				{error ? (
					<EmptyState icon={<ErrorOutlineRoundedIcon/>} title="No se ha podido calcular"
								description="Vuelve a cargar la página; si sigue fallando, avisa con el botón de abajo a la derecha."/>
				) : !data ? <SkeletonRows rows={6}/> : all.length === 0 ? (
					<EmptyState icon={<TaskAltRoundedIcon/>} title="Ningún documento fuera de plazo"
								description="Todos los documentos de persona están dentro de su plazo de conservación."/>
				) : (
					<>
						<Box sx={{mb: 2}}><FilterChips options={options} value={rule} onChange={setRule}/></Box>
						{compact ? (
							<Stack spacing={1.5}>
								{rows.map(d => (
									<ListCard key={d.documentId} onClick={open && (() => open(d))}>
										<Person d={d}/>
										<Typography variant="body2" sx={{mt: 0.5}}>{d.title}</Typography>
										<Typography variant="caption" color="text.secondary" display="block">
											{d.typeName || "Sin tipo"} · subido el {dateToString(d.uploaded)}
										</Typography>
										<Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{mt: 1}}>
											<Rule d={d}/>
											<SoftChip tone="error" label={`Venció ${dateToString(d.dueDate)}`}/>
										</Stack>
									</ListCard>
								))}
							</Stack>
						) : (
							<TableContainer sx={tableContainerSx}>
								<Table size="small" sx={tableSx}>
									<TableHead>
										<TableRow>
											<TableCell>Persona</TableCell>
											<TableCell>Documento</TableCell>
											<TableCell sx={{width: 110}}>Subido</TableCell>
											<TableCell>Plazo</TableCell>
											<TableCell sx={{width: 110}}>Venció</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{rows.map(d => (
											<TableRow key={d.documentId} hover={!!open} onClick={open && (() => open(d))}
													  sx={{cursor: open ? "pointer" : undefined}}>
												<TableCell><Person d={d}/></TableCell>
												<TableCell>
													<Typography variant="body2">{d.title}</Typography>
													<Typography variant="caption" color="text.secondary">{d.typeName || "Sin tipo"}</Typography>
												</TableCell>
												<TableCell sx={{fontVariantNumeric: "tabular-nums"}}>{dateToString(d.uploaded)}</TableCell>
												<TableCell><Rule d={d}/></TableCell>
												<TableCell sx={{fontVariantNumeric: "tabular-nums"}}>{dateToString(d.dueDate)}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						)}
					</>
				)}
			</Panel>

			<ExpiredCandidatesPanel/>
		</Page>
	);
};
